/* sm-fit-engine.js
   Seth Michael fit engine. Pure math, no DOM, no network.
   All measurements in inches. Move-in Width is measured with legs off.
   Doorways: a straight shot checks width only. A doorway reached by a turn is tried
   flat on its back first, then on its end (stood up in the space in front of the door
   and tilted down through the opening, using the sofa's depth as its thickness).
   Verdicts: "fits" | "tight" | "no_fit". Reasons are codes; the UI owns the guest copy. */
(function (root) {
  'use strict';

  var RULES = {
    doorMargin: 0.5,        // clear opening must beat Move-in Width by this to be "fits"
    wallMargin: 2,          // wall must beat sofa width by this to be "fits"
    walkRoomNote: 12,       // less than this past the open bed is a soft note only
    ceiling: 96             // assumed ceiling in front of a door when a piece is stood on end
  };

  var RANK = { fits: 0, tight: 1, no_fit: 2 };
  function worse(a, b) { return RANK[a] >= RANK[b] ? a : b; }

  function num(v) {
    var n = typeof v === 'string' ? parseFloat(v) : v;
    return (typeof n === 'number' && isFinite(n) && n > 0 && n < 1000) ? n : null;
  }

  // Width-only opening: doorway, straight hall, straight stairs, elevator door.
  function opening(clear, moveIn) {
    if (clear === null) return { verdict: 'tight', reason: 'missing_measurement' };
    if (clear >= moveIn + RULES.doorMargin) return { verdict: 'fits', reason: 'opening_clear' };
    if (clear >= moveIn) return { verdict: 'tight', reason: 'opening_within_margin' };
    return { verdict: 'no_fit', reason: 'opening_too_narrow' };
  }

  // Longest piece of thickness w that clears a 90 degree turn from a corridor of
  // width a into one of width b, kept flat (on its back). Ignores tilting toward the
  // ceiling, so it errs safe.
  function maxCornerLength(a, b, w) {
    if (w >= a || w >= b) return 0;
    var best = Infinity, steps = 2000;
    for (var i = 1; i < steps; i++) {
      var t = (Math.PI / 2) * (i / steps);
      var s = Math.sin(t), c = Math.cos(t);
      var L = a / s + b / c - w / (s * c);
      if (L < best) best = L;
    }
    return best;
  }

  function corner(a, b, moveIn, length, inPieces) {
    if (a === null || b === null) return { verdict: 'tight', reason: 'missing_measurement' };
    var oa = opening(a, moveIn), ob = opening(b, moveIn);
    var widthVerdict = worse(oa.verdict, ob.verdict);
    if (widthVerdict === 'no_fit') return { verdict: 'no_fit', reason: 'opening_too_narrow' };
    if (inPieces) return { verdict: widthVerdict, reason: 'pieces_width_only' };
    var maxL = maxCornerLength(a, b, moveIn);
    if (length <= maxL) return { verdict: widthVerdict, reason: 'turn_clear', maxLength: round(maxL) };
    return { verdict: 'tight', reason: 'turn_needs_tilt', maxLength: round(maxL) };
  }

  // Doorway reached by a turn: flat on its back first, then on its end and tilted in.
  function doorTurn(step, moveIn, length, depth, inPieces) {
    var door = opening(num(step.width), moveIn);
    if (door.verdict === 'no_fit') return door;
    if (inPieces) return { verdict: door.verdict, reason: 'pieces_width_only' };
    var front = num(step.spaceInFront), doorW = num(step.width), doorH = num(step.doorHeight);
    if (front === null) return { verdict: 'tight', reason: 'missing_measurement' };
    var flatMax = maxCornerLength(front, doorW, moveIn);
    if (length <= flatMax) return { verdict: door.verdict, reason: 'turn_clear', maxLength: round(flatMax) };
    if (doorH === null || depth === null) return { verdict: 'tight', reason: 'missing_measurement' };
    var endMax = maxCornerLength(front, doorH, depth);
    if (length <= endMax && length <= RULES.ceiling) return { verdict: door.verdict, reason: 'on_end_clear', maxLength: round(endMax) };
    return { verdict: 'tight', reason: 'turn_too_tight', maxLength: round(Math.max(flatMax, endMax)) };
  }

  function elevator(step, moveIn, length, inPieces) {
    var door = opening(num(step.doorWidth), moveIn);
    if (door.verdict === 'no_fit') return door;
    if (inPieces) return { verdict: door.verdict, reason: 'pieces_width_only' };
    var w = num(step.cabWidth), d = num(step.cabDepth), h = num(step.cabHeight);
    if (w === null || d === null) return { verdict: 'tight', reason: 'missing_measurement' };
    var floorDiag = Math.sqrt(w * w + d * d);
    if (length <= floorDiag) return { verdict: door.verdict, reason: 'cab_floor_clear' };
    if (h !== null && length <= h) return { verdict: door.verdict, reason: 'cab_on_end_clear' };
    return { verdict: 'tight', reason: 'cab_too_small' };
  }

  function pathStep(step, moveIn, length, depth, inPieces) {
    switch (step && step.type) {
      case 'door':
        if (step.straight === true) return opening(num(step.width), moveIn);
        if (step.straight === false) return doorTurn(step, moveIn, length, depth, inPieces);
        return { verdict: 'tight', reason: 'straight_unanswered' };
      case 'hall':            return opening(num(step.width), moveIn);
      case 'stairs_straight': return opening(num(step.width), moveIn);
      case 'turn':            return corner(num(step.fromWidth), num(step.toWidth), moveIn, length, inPieces);
      case 'stairs_landing':  return corner(num(step.fromWidth), num(step.toWidth), moveIn, length, inPieces);
      case 'stairs_winding':  return { verdict: 'tight', reason: 'winding_stairs' };
      case 'elevator':        return elevator(step, moveIn, length, inPieces);
      default:                return { verdict: 'tight', reason: 'unknown_step' };
    }
  }

  function roomCheck(room, width, openDepth, isSleeper) {
    var out = [], wall = num(room && room.wallLength), floor = num(room && room.floorDepth);
    if (wall !== null) {
      if (wall >= width + RULES.wallMargin) out.push({ check: 'wall', verdict: 'fits', reason: 'wall_clear' });
      else if (wall >= width) out.push({ check: 'wall', verdict: 'tight', reason: 'wall_within_margin' });
      else out.push({ check: 'wall', verdict: 'no_fit', reason: 'wall_too_short' });
    }
    if (floor !== null && openDepth !== null) {
      if (floor < openDepth) {
        out.push({ check: 'floor', verdict: 'no_fit', reason: isSleeper ? 'bed_does_not_open' : 'does_not_recline' });
      } else {
        var spare = floor - openDepth;
        out.push({ check: 'floor', verdict: 'fits', reason: 'floor_clear', spare: round(spare),
                   note: spare < RULES.walkRoomNote ? 'little_walk_room' : null });
      }
    }
    return out;
  }

  function round(n) { return Math.round(n * 100) / 100; }

  /* product: { kind: 'sleeper'|'recliner'|'daydreamer', shipsInPieces, moveIn, openDepth, depth,
                sizes: [{ key, label, width }] }
     door step: { type: 'door', width, straight: true|false, spaceInFront, doorHeight }
     answers: { path: [step...], room: { wallLength, floorDepth } } */
  function evaluate(product, answers) {
    var moveIn = num(product && product.moveIn);
    if (moveIn === null) return { ok: false, error: 'product_missing_move_in' };
    var openDepth = num(product.openDepth);
    var depth = num(product.depth);
    var inPieces = !!product.shipsInPieces;
    var isSleeper = product.kind === 'sleeper';
    var path = (answers && answers.path) || [];

    var sizes = (product.sizes || []).filter(function (s) { return num(s.width) !== null; }).map(function (s) {
      var width = num(s.width), verdict = 'fits', checks = [];
      path.forEach(function (step, i) {
        var r = pathStep(step, moveIn, width, depth, inPieces);
        r.check = 'path'; r.step = i; r.type = step && step.type;
        checks.push(r); verdict = worse(verdict, r.verdict);
      });
      roomCheck(answers && answers.room, width, openDepth, isSleeper).forEach(function (r) {
        checks.push(r); verdict = worse(verdict, r.verdict);
      });
      return { key: s.key, label: s.label, width: width, verdict: verdict, checks: checks };
    });

    if (!sizes.length) return { ok: false, error: 'product_missing_width' };

    var overall = sizes.reduce(function (acc, s) { return RANK[s.verdict] < RANK[acc] ? s.verdict : acc; }, 'no_fit');
    var passing = sizes.filter(function (s) { return s.verdict === 'fits'; })
                       .sort(function (a, b) { return b.width - a.width; })
                       .map(function (s) { return s.key; });

    return { ok: true, best: overall, sizes: sizes, passingSizes: passing, rules: RULES };
  }

  var api = { evaluate: evaluate, maxCornerLength: maxCornerLength, RULES: RULES };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.SMFit = api;
})(typeof window !== 'undefined' ? window : this);
